import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import './primitives.css';
import { t } from '../../i18n/index.ts';

/**
 * Componentes base (H-004). Nenhum aceita cor, espaçamento ou tipo por
 * propriedade: quem precisa de um aspecto novo acrescenta uma variante aqui,
 * onde ela fica visível a toda a gente, em vez de o resolver localmente.
 */

type ButtonVariant = 'primary' | 'secondary' | 'ghost';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  block?: boolean;
}

export function Button({ variant = 'primary', block = false, className, ...rest }: ButtonProps) {
  const classes = ['btn', `btn--${variant}`, block ? 'btn--block' : null, className]
    .filter(Boolean)
    .join(' ');
  return <button type="button" className={classes} {...rest} />;
}

export function Card({ children, flush = false }: { children: ReactNode; flush?: boolean }) {
  return <div className={flush ? 'card card--flush' : 'card'}>{children}</div>;
}

interface ProgressBarProps {
  value: number;
  max: number;
  /**
   * Rótulo textual visível. **Omitir mostra só a barra**, e é o que o produto
   * quer enquanto se avança — decisão do dono do produto, 15/08/2026: durante as
   * lições não aparece «1 de 6», a barra apenas cresce.
   *
   * A contagem não desaparece para quem não vê a barra: vive nos
   * `aria-valuenow`/`aria-valuemax` abaixo, que são o que um leitor de ecrã
   * anuncia. Trocar um pelo outro seria esconder a informação a quem mais
   * precisa dela (RNF-023).
   *
   * Continua a usar-se onde o texto diz alguma coisa que a barra não diz — o
   * objectivo diário diz «falta 1 lição», e isso não é uma contagem de posição.
   */
  label?: string;
}

export function ProgressBar({ value, max, label }: ProgressBarProps) {
  const safeMax = Math.max(max, 1);
  const pct = Math.min(100, Math.max(0, (value / safeMax) * 100));
  return (
    <div className="progress">
      <div
        className="progress__track"
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-label={t('progresso.aria')}
      >
        <div className="progress__fill" style={{ width: `${pct}%` }} />
      </div>
      {label === undefined ? null : <span className="progress__label">{label}</span>}
    </div>
  );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  code?: boolean;
}

export function Field({ label, hint, code = false, id, ...rest }: FieldProps) {
  const inputId = id ?? `campo-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const hintId = hint ? `${inputId}-dica` : undefined;
  return (
    <div className="field">
      <label className="field__label" htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        className={code ? 'field__input field__input--code' : 'field__input'}
        aria-describedby={hintId}
        {...rest}
      />
      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

type CalloutTone = 'info' | 'success' | 'error';

const TONE_ICON: Record<CalloutTone, string> = {
  info: 'i',
  success: '✓',
  error: '!',
};

export function Callout({
  tone = 'info',
  children,
  role,
}: {
  tone?: CalloutTone;
  children: ReactNode;
  role?: 'status' | 'alert';
}) {
  return (
    <div className={`callout callout--${tone}`} role={role}>
      <span className="callout__icon" aria-hidden="true">
        {TONE_ICON[tone]}
      </span>
      <div className="callout__body">{children}</div>
    </div>
  );
}
